<template>
    <div class="o-knowledgeBase u-w100 u-py20 u-overflowYAuto">
        <div class="o-knowledgeBase__inner u-container u-overflowHidden">
            <form @submit.prevent="handleSubmit">
                <div class="o-knowledgeBase__searchHolder a-searchBox u-mb20 u-posRelative">
                    <div class="a-searchBox__icon">
                        <i class="bx bx-search"></i>
                    </div>
                    <input type="text" v-model="searchQuery" class="a-searchBox__input" :placeholder="$t('knowledgebase.search')">
                    <button type="submit" class="u-hidden -mobile">Search</button>
                    <div v-if="searchQuery" class="a-searchBox__clear" @click="clearSearch">
                        <i class='bx bx-x'></i>
                    </div>
                </div>
            </form>
            <div class="o-knowledgeBase__searchOverview" v-if="!loading && submitted && searchQuery && Object.keys(searchResults).length > 0">
                <div v-for="(items, category, index) in searchResults" :key="index">
                    <div class="o-knowledgeBase__searchCategory u-mb30" v-if="items.length > 0">
                        <h2 class="o-knowledgeBase__searchCategoryTitle u-colorSecondary">{{ translatedTitle(category) }}</h2>
                        <div v-for="item in items" :key="item.id">
                            <component :is="getComponent(item.post_type)" :item="item" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="o-knowledgeBase__overview">
                <div class="o-knowledgeBase__tiles u-grid">
                    <Tile :title="$t('knowledgebase.jargonCards')" href="/#/jargoncards" class="-circles" />
                    <Tile :title="$t('knowledgebase.videos')" href="/#/videos" class="-circlesOverlap" />
                    <Tile :title="$t('knowledgebase.importantContacts')" href="/#/importantcontacts" class="-badges -light" />
                    <Tile :title="$t('knowledgebase.articles')" href="/#/articles" class="-cards" />
                </div>
                <div v-if="!loading && featuredArticles.length > 0" class="o-knowledgeBase__featured">
                    <h2 class="o-knowledgeBase__title u-colorSecondary u-mt20">
                        {{ $t('knowledgebase.featuredArticles') }}
                    </h2>
                    <div class="o-knowledgeBase__articles">
                        <Article v-for="article in featuredArticles" :key="article.id" :item="article" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import api from '../services/api';
import { useI18n } from 'vue-i18n';

// Components
import Tile from '../components/molecules/Tile.vue';
import Article from '../components/molecules/Article.vue';
import JargonCard from '../components/molecules/JargonCard.vue';
import Video from '../components/molecules/Video.vue';
import Contact from '../components/molecules/Contact.vue';

const featuredArticles = ref([]);
const loading = ref(true);
const searchQuery = ref('');
const searchResults = ref({});
const submitted = ref(false);
const { t } = useI18n();
const emit = defineEmits(['updateHeader']);

const originalTitle = t('knowledgebase.knowledgebase');

function onSearch(query) {
    if (!query) {
        emit('updateHeader', originalTitle);
    }
}

const fetchFeaturedArticles = async () => {
    try {
        const response = await api.get('/featured-articles');

        if (response.status === 200 && Object.keys(response.data).length !== 0) {
            featuredArticles.value = Object.values(response.data).map(article => ({
                id: article.id,
                image: article.image,
                title: article.title,
                date: article.date,
                categories: article.categories,
            }));
        }
        loading.value = false;
    } catch (error) {
        console.error('Error fetching featured articles:', error);
        loading.value = false;
    }
};

const handleSubmit = async (e) => {
    e.preventDefault();
    emit('updateHeader', t('knowledgebase.searchResults'));
    submitted.value = true;
    try {
        const response = await api.get('/search-knowledgebase', {
            params: {
                query: searchQuery.value,
            },
        });

        if (response.status === 200 && Object.keys(response.data).length !== 0) {
            searchResults.value = response.data;
        }
    } catch (error) {
        console.error('Error searching knowledge base:', error);
    }
};

const clearSearch = () => {
    searchQuery.value = '';
    submitted.value = false;
    searchResults.value = {};
    onSearch(searchQuery.value);
};

const getComponent = (postType) => {
    switch (postType) {
        case 'importantcontact':
            return Contact;
        case 'knowledgebase':
            return Article;
        case 'jargoncard':
            return JargonCard;
        case 'video':
            return Video;
        default:
            return null;
    }
};

const translatedTitle = computed(() => {
    return (category) => {
        const titles = {
            importantcontact: t('knowledgebase.importantContacts'),
            knowledgebase: t('knowledgebase.articles'),
            jargoncard: t('knowledgebase.jargonCards'),
            video: t('knowledgebase.videos')
        };
        return titles[category] || category;
    };
});

watch(searchQuery, (newValue) => {
    if (newValue === '') {
        submitted.value = false;
        searchResults.value = {};
    }
    onSearch(newValue);
});

onMounted(() => {
    fetchFeaturedArticles();
    onSearch(searchQuery.value);
});
</script>